import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Tag, Input, TreeSelect, Spin, Space, Typography } from 'antd';
import { RightOutlined, UpOutlined, SearchOutlined } from '@ant-design/icons';
import TreeSelectWrapper from 'components/Treewrapper/Treewrapper';

function EbzTreeSelect({
  merchants,
  onChange,
  fetchMerchantList,
  merchantLoading,
  setMerchantPagination,
  merchantTransactionListRef,
  formName,
  selectedMerchantTotalCounts,
  inputSearchPlaceholder,
  inputPlaceholder
}) {
  const [merchantData, setMerchantData] = useState([]);
  const [selectedValues, setSelectedValues] = useState([]);
  const [open, setOpen] = useState(false);
  // const [allIds, setAllIds] = useState([]);
  const scrollHandlerRef = useRef(null);

  useEffect(() => {
    if (!merchantLoading && merchants.current.length > 0) {
      const uniqueMerchantsMap = new Map();
      merchants.current.forEach((e) => {
        if (!uniqueMerchantsMap.has(e.value)) {
          uniqueMerchantsMap.set(e.value, {
            title: `${e.label} - ${e.value} - ${e.email}`,
            value: e.value,
            id: e.value
          });
        }
      });

      const uniqueMerchantArray = Array.from(uniqueMerchantsMap.values());
      setMerchantData(uniqueMerchantArray);
      // setAllIds(uniqueMerchantArray);
    }
  }, [merchantLoading && merchants]);

  const tagRender = (props) => {
    // eslint-disable-next-line react/prop-types
    const { label, closable, onClose } = props;

    return (
      <Tag
        color="#FFFFFF"
        closable={closable}
        id="565w"
        onClose={onClose}
        style={{
          color: '#091B49',
          background: 'var(--color-fill-alter)',
          border: '1px solid #091B49',
          cursor: 'default'
        }}
      >
        {label}
      </Tag>
    );
  };

  const updateMerchantRef = (key, data) => {
    /* eslint-disable no-param-reassign */
    merchantTransactionListRef.current = {
      ...merchantTransactionListRef.current,
      [key]: data
    };
  };

  const handleSearch = (e) => {
    const v = e.target.value?.toLowerCase();
    const data = {
      current: 1,
      pageSize: 3000,
      showSizeChanger: false,
      total: 0
    };
    const removeSpace = v.trim();
    if (removeSpace && removeSpace.length > 0) {
      const hasSearchTerm = (n) => n.toLowerCase().indexOf(v) !== -1;
      const filterData = (arr) => arr?.filter((n) => hasSearchTerm(n.title));
      const filteredData = filterData(merchantData).map((n) => {
        return {
          ...n,
          children: filterData(n.children)
        };
      });
      if (filteredData.length > 0) {
        updateMerchantRef(formName, { data: filteredData });
        setMerchantData(filteredData);
      } else {
        setMerchantData([]);
        // setAllIds([]);
        setSelectedValues([]);
        updateMerchantRef(formName, { data: null });
        fetchMerchantList(data, v);
      }
    } else {
      setMerchantData([]);
      // setAllIds([]);
      setSelectedValues([]);
      updateMerchantRef(formName, { data: null });
      fetchMerchantList(data);
    }
  };

  const handleMerchantInfinityCall = () => {
    setMerchantPagination((prev) => {
      const updated = {
        ...prev,
        pagination: {
          ...prev.pagination,
          current: prev.pagination.current + 1
        }
      };

      if (updated.pagination.hasNext) {
        // Call API with updated pagination (optional)
        console.log('Reached bottom — fetch more merchants!');
        fetchMerchantList(updated.pagination);
      }

      return updated;
    });
  };

  const handleDropDownRender = (menu) => {
    return (
      <div className={`merchant-dropdown-${formName}`}>
        <div style={{ padding: '10px 10px' }}>
          <Input
            prefix={<SearchOutlined />}
            placeholder={inputSearchPlaceholder}
            onChange={handleSearch}
            style={{
              width: '100%'
            }}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
          />
        </div>
        {menu}
        {merchantLoading && (
          <div style={{ padding: '8px', textAlign: 'center' }}>
            <Spin size="small" />
          </div>
        )}
      </div>
    );
  };

  // const handleSelection = () => {
  //   const dataSelectedValues =
  //     selectedValues?.length > 0 ? [] : allIds.map((merchantId) => merchantId.value);
  //   onChange(dataSelectedValues);
  //   setSelectedValues(selectedValues?.length > 0 ? [] : allIds);
  // };

  const handleDropdownVisibleChange = (visible) => {
    setOpen(visible);
    setTimeout(() => {
      const scrollContainer = document.querySelector(
        `.merchant-dropdown-${formName} .ant-select-tree-list-holder`
      );
      let debounceTimer;
      if (visible && scrollContainer) {
        const onScroll = () => {
          clearTimeout(debounceTimer);
          debounceTimer = setTimeout(() => {
            const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
            if (scrollTop + clientHeight >= scrollHeight - 20) {
              handleMerchantInfinityCall();
            }
          }, 200);
        };

        scrollContainer.addEventListener('scroll', onScroll);
        scrollHandlerRef.current = {
          ...scrollHandlerRef.current,
          [formName]: onScroll
        };
      } else if (scrollContainer && scrollHandlerRef.current[formName]) {
        scrollContainer.removeEventListener('scroll', scrollHandlerRef.current[formName]);
        scrollHandlerRef.current[formName] = null;
      }
    }, 100); // delay for dropdown DOM to mount
  };

  const refData = merchantTransactionListRef.current?.[formName]?.data;
  const finalTreeData = Array.isArray(refData) && refData.length > 0 ? refData : merchantData;

  return (
    <TreeSelectWrapper
      placeholder={inputPlaceholder}
      treeCheckable
      showCheckedStrategy={TreeSelect.SHOW_CHILD}
      dropdownStyle={{ maxHeight: '500px' }}
      onChange={(ids) => {
        const filterId = ids.map((merchantId) => merchantId.value);
        onChange(filterId);
        setSelectedValues(ids);
      }}
      value={selectedValues}
      labelInValue
      tagRender={tagRender}
      suffixIcon={
        <Tag
          style={{
            backgroundColor: 'transparent',
            borderColor: 'transparent',
            display: 'contents'
          }}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setOpen(!open)}
        >
          {open ? <UpOutlined /> : <RightOutlined />}
        </Tag>
      }
      showArrow
      open={open}
      onDropdownVisibleChange={(visible) => handleDropdownVisibleChange(visible)}
      dropdownRender={(menu) => handleDropDownRender(menu)}
      treeData={[
        {
          title: (
            <Space style={{ gap: '250px' }}>
              {/* <span
                role="presentation"
                onClick={() => {
                  handleSelection();
                }}
                style={{
                  display: 'inline-block',
                  color: 'var(--color-primary)',
                  cursor: 'pointer'
                }}
              >
                {selectedValues.length > 0 ? 'Unselect all' : 'Select all'}
              </span> */}
              <span>
                <Typography.Text>{`(${selectedValues.length} / ${selectedMerchantTotalCounts})`}</Typography.Text>
              </span>
            </Space>
          ),
          value: 'NA',
          disableCheckbox: true,
          disabled: true
        },
        ...finalTreeData
      ]}
    />
  );
}

EbzTreeSelect.propTypes = {
  merchants: PropTypes.instanceOf(Object).isRequired,
  onChange: PropTypes.func.isRequired,
  fetchMerchantList: PropTypes.func.isRequired,
  merchantLoading: PropTypes.bool.isRequired,
  setMerchantPagination: PropTypes.func.isRequired,
  formName: PropTypes.string.isRequired,
  merchantTransactionListRef: PropTypes.instanceOf(Object).isRequired,
  selectedMerchantTotalCounts: PropTypes.number.isRequired,
  inputSearchPlaceholder: PropTypes.string.isRequired,
  inputPlaceholder: PropTypes.string.isRequired
};
export default EbzTreeSelect;
